import { lazy, useEffect, useMemo, useState } from 'react'
import { KeycloakAdminService } from '@nv/auth'
import { NVButton } from '@nv/basic-components'
import { ActionDropdown, ListTable, useNVModal } from '@nv/components'
import AddAdminIcon from '@nv/images/design/add-admin-icon'
import { AdminVoterAuthService } from '@nv/services'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import styles from './AdminUserList.module.scss'
const NewAdminUser = lazy(() => import('@nv/lazy-loadable/lib/modals/NewAdminUser'))
const ConfirmPWResetAdmin = lazy(() => import('@nv/lazy-loadable/lib/modals/ConfirmPWResetAdmin'))
const ConfirmUnlockAdmin = lazy(() => import('@nv/lazy-loadable/lib/modals/ConfirmUnlockAdmin'))
const ConfirmRegenQrAdmin = lazy(() => import('@nv/lazy-loadable/lib/modals/ConfirmRegenQrAdmin'))
const ConfirmDeleteAdmin = lazy(() => import('@nv/lazy-loadable/lib/modals/ConfirmDeleteAdmin'))
const ConfirmLockAdmin = lazy(() => import('@nv/lazy-loadable/lib/modals/ConfirmLockAdmin'))

export const initialValues = {}
export const loginSchema = Yup.object().shape({})

export const AdminUserList = () => {
  const [users, setUsers] = useState([])
  const { t, i18n } = useTranslation()
  const [adminRoles, setAdminRoles] = useState([])
  const [refreshList, setRefreshList] = useState(false)
  const currentLanguage = i18n.language
  const [loading, setLoading] = useState(true)
  const [selectedUser, setSelectedUser] = useState(null)
  const { NVModal: NewAdminUserModal, openModal: openNewAdminUserModal } = useNVModal()
  const { NVModal: ConfirmResetPasswordModal, openModal: openConfirmResetPasswordModal } = useNVModal()
  const { NVModal: ConfirmUnlockAdminModal, openModal: openConfirmUnlockAdminModal } = useNVModal()
  const { NVModal: ConfirmRegenQrAdminModal, openModal: openConfirmRegenQrAdminModal } = useNVModal()
  const { NVModal: ConfirmDeleteAdminModal, openModal: openConfirmDeleteAdminModal } = useNVModal()
  const { NVModal: ConfirmLockAdminModal, openModal: openConfirmLockAdminModal } = useNVModal()

  const columns = useMemo(() => {
    return [
      {
        accessorKey: 'username',
        cell: (info) => info.row.original.username || '',
        header: () => t('form.labels.username'),
      },
      {
        accessorKey: 'role',
        cell: (info) => (info.row.original.roles?.length ? info.row.original.roles[0].displayName[currentLanguage] : ''),
        header: () => t('admin.dropdown.role'),
      },
      {
        accessorKey: 'requests',
        cell: (info) => formatSupportRequests(info.row.original) || '',
        header: () => t('admin.requests'),
      },
      {
        accessorKey: 'status',
        cell: (info) => (
          <span className={clsx(styles['type__label'], styles[`type__label--${info.row.original.enabled}`])}>
            {info.row.original.enabled ? t('main.enabled') : t('main.locked')}
          </span>
        ),
        header: () => t('admin.status'),
      },
      {
        accessorKey: 'action',
        cell: (info) => {
          const user = info.row.original
          const adminUserOptions = [
            {
              value: 'Reset Password',
              label: t('admin.users.actions.resetPassword'),
              callback: async () => {
                setSelectedUser(user)
                openConfirmResetPasswordModal()
              },
            },
            {
              value: 'Unlock Account',
              label: t('admin.users.actions.unlockAccount'),
              callback: async () => {
                setSelectedUser(user)
                openConfirmUnlockAdminModal()
              },
            },

            {
              value: 'Regenerate QR Code',
              label: t('auth.qrCode.regen'),
              callback: async () => {
                setSelectedUser(user)
                openConfirmRegenQrAdminModal()
              },
            },
          ]
          if (KeycloakAdminService.isSuperAdmin()) {
            adminUserOptions.push({
              value: 'Lock Account',
              label: t('admin.users.actions.lockAccount'),
              callback: async () => {
                setSelectedUser(user)
                openConfirmLockAdminModal()
              },
            })
          }
          if (KeycloakAdminService.isSuperAdmin()) {
            adminUserOptions.push({
              value: 'Delete User',
              label: t('admin.dropdown.deleteUser'),
              callback: async () => {
                setSelectedUser(user)
                openConfirmDeleteAdminModal()
              },
            })
          }

          return (
            <ActionDropdown
              values={adminUserOptions}
              onCallback={(value) => {
                const selectedOption = adminUserOptions.find((opt) => opt.value === value)
                selectedOption?.callback()
              }}
            />
          )
        },
        header: () => t('buttons.action'),
      },
    ]
  }, [currentLanguage])

  const fetchUsers = async () => {
    const resp = await AdminVoterAuthService.adminFetchAdminUsers()
    if (resp.success) {
      // @ts-ignore
      setUsers(resp.data)
      setRefreshList(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    const fetchRoles = async () => {
      const resp = await AdminVoterAuthService.adminFetchRealmRoles()
      if (resp.success) {
        let roles = resp.data ? resp.data : []
        //@ts-ignore
        roles = roles.map((r) => {
          const role = r
          role.value = { name: r.name, id: r.id }
          role.label = r.displayName[currentLanguage]
          return role
        })
        //@ts-ignore
        roles = roles.filter((r) => (r.label ? true : false))
        //@ts-ignore
        setAdminRoles(roles)
      }
    }
    fetchRoles()
  }, [currentLanguage])

  useEffect(() => {
    fetchUsers()
  }, [refreshList])

  const formatSupportRequests = (user) => {
    const REQUEST_TYPES = {
      RESET_PASSWORD: t('admin.users.actions.resetPassword'),
      UNLOCK_ACCOUNT: t('admin.users.actions.unlockAccount'),
      REGENERATE_QR_CODE: t('auth.qrCode.regenQrCode'),
    }
    const requests = user.attributes?.authSupportRequests?.map((r) => (REQUEST_TYPES[r] ? REQUEST_TYPES[r] : r))
    return requests?.join(', ')
  }

  return (
    <>
      <NewAdminUserModal>
        <NewAdminUser rolesOptions={adminRoles} setRefreshList={setRefreshList} />
      </NewAdminUserModal>

      <ConfirmResetPasswordModal>
        <ConfirmPWResetAdmin selectedUser={selectedUser} fetchUsers={fetchUsers} />
      </ConfirmResetPasswordModal>

      <ConfirmUnlockAdminModal>
        <ConfirmUnlockAdmin selectedUser={selectedUser} fetchUsers={fetchUsers} />
      </ConfirmUnlockAdminModal>

      <ConfirmRegenQrAdminModal>
        <ConfirmRegenQrAdmin selectedUser={selectedUser} fetchUsers={fetchUsers} />
      </ConfirmRegenQrAdminModal>

      <ConfirmDeleteAdminModal>
        <ConfirmDeleteAdmin selectedUser={selectedUser} fetchUsers={fetchUsers} />
      </ConfirmDeleteAdminModal>

      <ConfirmLockAdminModal>
        <ConfirmLockAdmin selectedUser={selectedUser} fetchUsers={fetchUsers} />
      </ConfirmLockAdminModal>

      <div className={styles['container']}>
        <div className={styles['header']}>
          <h1>{t('admin.users.users')}</h1>

          {KeycloakAdminService.isSuperAdmin() && (
            <NVButton
              variant='primaryGrey'
              text={t('admin.users.newAdminUser')}
              height={40}
              onClick={() => openNewAdminUserModal()}
              icon={<AddAdminIcon />}
              ariaLabel={t('Add Admin button')}
            />
          )}
        </div>

        <ListTable data={users} columns={columns} loading={loading} />
      </div>
    </>
  )
}
